import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Building2, BarChart2, Network, HeartHandshakeIcon, SparklesIcon } from 'lucide-react';
import { cn } from '../lib/utils';
interface WhatWeDoFeatureProps {
  className?: string;
  circleText?: string;
  badgeTexts?: {
    first: string;
    second: string;
    third: string;
  };
  buttonTexts?: {
    first: string;
    second: string;
  };
  title?: string;
  description?: string;
  lightColor?: string;
}
const WhatWeDoFeature = ({
  className,
  circleText,
  badgeTexts,
  buttonTexts,
  title,
  description,
  lightColor
}: WhatWeDoFeatureProps) => {
  return <div className={cn('relative flex h-[250px] sm:h-[300px] w-full max-w-[800px] flex-col items-center', className)} data-id="element-310" data-mp="22ys5yocx313f">
      {/* SVG Paths */}
      <svg className="h-full w-full text-muted" width="100%" height="100%" viewBox="0 0 200 100" data-id="element-311" data-mp="15voaioi50ac">
        <defs data-id="element-312" data-mp="64f92frd0614">
          <linearGradient id="pathGradient" gradientUnits="userSpaceOnUse" data-id="element-313" data-mp="62uhmz0cd37h">
            <stop offset="0%" stopColor="rgba(37, 99, 235, 0)" data-id="element-314" data-mp="3f487ktq8u32j" />
            <stop offset="50%" stopColor="rgba(37, 99, 235, 1)" data-id="element-315" data-mp="3a7rinxb7zkoe" />
            <stop offset="100%" stopColor="rgba(37, 99, 235, 0)" data-id="element-316" data-mp="3ovw3b6tu269t" />
            <animate attributeName="x1" values="-100%;0%;100%" dur="2s" repeatCount="indefinite" data-id="element-317" data-mp="3ov2tfwzkv0ng" />
            <animate attributeName="x2" values="0%;100%;200%" dur="2s" repeatCount="indefinite" data-id="element-318" data-mp="2w89pz6uszn9r" />
          </linearGradient>
          {/* Masks and gradients */}
          <mask id="db-mask-1" data-id="element-319" data-mp="3597wpwwmm8w1">
            <path d="M 31 10 v 15 q 0 5 5 5 h 59 q 5 0 5 5 v 10" strokeWidth="0.5" stroke="white" data-id="element-320" data-mp="316ba7v3ytsmt" />
          </mask>
          <mask id="db-mask-2" data-id="element-321" data-mp="kfupwp64m8qq">
            <path d="M 100 10 v 35" strokeWidth="0.5" stroke="white" data-id="element-322" data-mp="ggcfinbxlg25" />
          </mask>
          <mask id="db-mask-3" data-id="element-323" data-mp="gif6b9kwrd7x">
            <path d="M 170 10 v 15 q 0 5 -5 5 h -60 q -5 0 -5 5 v 10" strokeWidth="0.5" stroke="white" data-id="element-324" data-mp="39bcbi9sjgux1" />
          </mask>
          <radialGradient id="db-blue-grad" fx="1" data-id="element-325" data-mp="3j8qo1ybcczk2">
            <stop offset="0%" stopColor={lightColor || '#2563eb'} data-id="element-326" data-mp="3j98kow1x1fsi" />
            <stop offset="100%" stopColor="transparent" data-id="element-327" data-mp="3nalrb8cu1n0h" />
          </radialGradient>
        </defs>
        {/* Base paths */}
        <g stroke="rgba(255,255,255,0.1)" fill="none" strokeWidth="0.4" data-id="element-328" data-mp="2qlcn7p4ivjuo">
          <path d="M 31 10 v 15 q 0 5 5 5 h 59 q 5 0 5 5 v 10" data-id="element-329" data-mp="2vicx6b4ht6r2" />
          <path d="M 100 10 v 35" data-id="element-330" data-mp="2vgs4kr1qnxvu" />
          <path d="M 170 10 v 15 q 0 5 -5 5 h -60 q -5 0 -5 5 v 10" data-id="element-331" data-mp="2zkhlqvg33d6u" />
        </g>
        {/* Animated paths */}
        <g stroke="url(#pathGradient)" fill="none" strokeWidth="0.4" data-id="element-332" data-mp="34chr53exayat">
          <path d="M 31 10 v 15 q 0 5 5 5 h 59 q 5 0 5 5 v 10" data-id="element-333" data-mp="27u3fnlebb55q" />
          <path d="M 100 10 v 35" data-id="element-334" data-mp="27rh83lrf8pni" />
          <path d="M 170 10 v 15 q 0 5 -5 5 h -60 q -5 0 -5 5 v 10" data-id="element-335" data-mp="2bunaujfbryp8" />
        </g>
        {/* Blue Lights */}
        <g mask="url(#db-mask-1)" data-id="element-336" data-mp="24bw72dfdfqkr">
          <circle className="database db-light-1" cx="0" cy="0" r="12" fill="url(#db-blue-grad)" data-id="element-337" data-mp="24auvm7ug122i" />
        </g>
        <g mask="url(#db-mask-2)" data-id="element-338" data-mp="1v9my3rzr459h">
          <circle className="database db-light-2" cx="0" cy="0" r="12" fill="url(#db-blue-grad)" data-id="element-339" data-mp="1vd2c0rhov89b" />
        </g>
        <g mask="url(#db-mask-3)" data-id="element-340" data-mp="1lecy19swy7yc">
          <circle className="database db-light-3" cx="0" cy="0" r="12" fill="url(#db-blue-grad)" data-id="element-341" data-mp="1gm1wk2iiye3o" />
        </g>
        {/* Buttons */}
        <g stroke="currentColor" fill="none" strokeWidth="0.4" data-id="element-342" data-mp="17ln84ua9kivy">
          {/* First Button */}
          <g data-id="element-343" data-mp="12majq53qv3r9">
            <rect fill="#18181B" x="14" y="5" width="37" height="10" rx="5" data-id="element-344" data-mp="12mtrwu3leds3"></rect>
            <FeatureIcon x="18" y="7.5" icon="acquire" data-id="element-345" data-mp="2xpgpubmfr63t"></FeatureIcon>
            <text x="28" y="12" fill="white" stroke="none" fontSize="5" fontWeight="500" data-id="element-346" data-mp="2xq8ltnjy35um">
              {badgeTexts?.first || 'Acquire'}
            </text>
          </g>
          {/* Second Button */}
          <g data-id="element-347" data-mp="2sww1hpmatl7o">
            <rect fill="#18181B" x="83" y="5" width="45" height="10" rx="5" data-id="element-348" data-mp="2swdupz20foli"></rect>
            <FeatureIcon x="87" y="7.5" icon="consolidate" data-id="element-349" data-mp="2j1vpgpgy3kzl"></FeatureIcon>
            <text x="97" y="12" fill="white" stroke="none" fontSize="5" fontWeight="500" data-id="element-350" data-mp="251t9j8s771mz">
              {badgeTexts?.second || 'Consolidate'}
            </text>
          </g>
          {/* Third Button */}
          <g data-id="element-351" data-mp="87gaoun2xwi1">
            <rect fill="#18181B" x="150" y="5" width="40" height="10" rx="5" data-id="element-352" data-mp="87zlzsi85mn3"></rect>
            <FeatureIcon x="154" y="7.5" icon="integrate" data-id="element-353" data-mp="1nsibaljilghy"></FeatureIcon>
            <text x="164" y="12" fill="white" stroke="none" fontSize="5" fontWeight="500" data-id="element-354" data-mp="22g3hioborvu3">
              {badgeTexts?.third || 'Integrate'}
            </text>
          </g>
        </g>
      </svg>
      {/* Main Box */}
      <div className="absolute bottom-0 sm:bottom-10 flex w-full flex-col items-center" data-id="element-355" data-mp="26iq8yo4g3eqr">
        {/* box title */}
        <div className="absolute -top-3 z-20 flex items-center justify-center rounded-lg border border-gray-700 bg-gray-900 px-2 py-1 sm:-top-4 sm:py-1.5" data-id="element-356" data-mp="2ldwi3jyu7tob">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uYkAKCz9MEFJTpGQ4cKVHd/ISRAELCIVILIANTRAININGBLUEANDWHITE.png" alt="Israeli Civilian Training Foundation" className="h-4 sm:h-5 w-auto object-contain" data-id="element-357" data-mp="r16jwq1fryb4" />
        </div>
        {/* box outer circle */}
        <div className="absolute -bottom-4 sm:-bottom-8 z-30 grid h-[40px] w-[40px] sm:h-[60px] sm:w-[60px] place-items-center rounded-full border-t border-gray-700 bg-gray-900" data-id="element-358" data-mp="1frawqsst60n4"></div>
        {/* box content */}
        <div className="relative z-10 flex h-[120px] sm:h-[150px] w-full items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-md" data-id="element-359" data-mp="1pqa6yft824c8">
          {/* Feature Icons */}
          <div className="relative w-full h-full flex items-center justify-center" data-id="element-360" data-mp="1ypn9eoixhsng">
            {/* Left Side Icons */}
            <div className="absolute left-2 sm:left-8 h-full flex flex-col justify-center gap-4 sm:gap-6" data-id="element-361" data-mp="23hyjulbaowu9">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-362" data-mp="28fq8n6gn82u8">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-363" data-mp="2dgnsrol7r6yd">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-364" data-mp="2if7djlo2mcp5" />
                </div>
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-365" data-mp="6sx55kts0pk8">
                  Professional Management
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-366" data-mp="3szbx53b8f8p5">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-367" data-mp="3nzpmn1msg6k1">
                  <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-368" data-mp="pi3kqyz9x11t" />
                </div>
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-369" data-mp="kjs7ee9rs8eu">
                  Industry Expertise
                </p>
              </div>
            </div>
            {/* Center Icon */}
            <div className="flex flex-col items-center justify-center relative z-20" data-id="element-370" data-mp="asnstfo0apkj">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-blue-400 flex items-center justify-center" data-id="element-371" data-mp="18939bz2uw8uk">
                <HeartHandshakeIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" data-id="element-372" data-mp="13ga8hmaidknr" />
              </div>
              <p className="mt-2 text-[10px] sm:text-xs text-blue-400 font-light" data-id="element-373" data-mp="bsqltx0tr9me">
                Patient Care
              </p>
            </div>
            {/* Right Side Icons */}
            <div className="absolute right-2 sm:right-8 h-full flex flex-col justify-center gap-4 sm:gap-6" data-id="element-374" data-mp="2uwwj61h9g8q">
              <div className="flex items-center justify-end gap-2 sm:gap-3" data-id="element-375" data-mp="7sokjoz04i7n">
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-376" data-mp="vjc6he5r58d7">
                  Systems & Processes
                </p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-377" data-mp="lk3euryd05u0">
                  <Network className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-378" data-mp="qi4wr9k1tz3w" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 sm:gap-3" data-id="element-379" data-mp="369asdtxjmn7x">
                <p className="text-[8px] sm:text-[10px] text-blue-400/80 font-light whitespace-nowrap" data-id="element-380" data-mp="3b9f7bfisz9w1">
                  Service Excellence
                </p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-400/20 flex items-center justify-center" data-id="element-381" data-mp="33rh39qje4r07">
                  <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" data-id="element-382" data-mp="3cq2jvg5axvyc" />
                </div>
              </div>
            </div>
          </div>
          {/* Background Circles */}
          <motion.div className="absolute -bottom-14 h-[100px] w-[100px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [0.98, 1.02, 0.98, 1, 1, 1, 1, 1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-383" data-mp="2k1dolxqxgkui" />
          <motion.div className="absolute -bottom-20 h-[145px] w-[145px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [1, 1, 1, 0.98, 1.02, 0.98, 1, 1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-384" data-mp="2ozyg98s45w7c" />
          <motion.div className="absolute -bottom-[100px] h-[190px] w-[190px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [1, 1, 1, 1, 1, 0.98, 1.02, 0.98, 1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-385" data-mp="39ltzp9k1695u" />
          <motion.div className="absolute -bottom-[120px] h-[235px] w-[235px] rounded-full border-t border-gray-700 bg-blue-900/10" animate={{
          scale: [1, 1, 1, 1, 1, 1, 0.98, 1.02, 0.98, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-386" data-mp="2vnnaeht5dhhs" />
        </div>
      </div>
    </div>;
};
export default WhatWeDoFeature;
const FeatureIcon = ({
  x = '0',
  y = '0',
  icon
}: {
  x: string;
  y: string;
  icon: string;
}) => {
  return <svg x={x} y={y} xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-387" data-mp="nefpg59kmaik">
      {icon === 'acquire' && <>
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2" data-id="element-388" data-mp="11dyp09obgdj9" />
          <path d="M9 15V9h6" data-id="element-389" data-mp="11c4dflyxpxru" />
          <path d="m9 9 6 6" data-id="element-390" data-mp="4o7vodrrgwf1" />
        </>}
      {icon === 'consolidate' && <>
          <path d="M3 3v18h18" data-id="element-391" data-mp="8p9vavplbahj" />
          <path d="m3 10 8-5 8 3 5-3" data-id="element-392" data-mp="8qkxws7auc72" />
          <path d="m3 17 8-5 8 3 5-4" data-id="element-393" data-mp="8ngjz3418237" />
        </>}
      {icon === 'integrate' && <>
          <path d="M2 12h20" data-id="element-394" data-mp="dmt77oytfb6b" />
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" data-id="element-395" data-mp="ifw7efy3livh" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" data-id="element-396" data-mp="ignsgwu2jy7i" />
          <path d="M6 15H4.5a2.5 2.5 0 0 0 0 5H6" data-id="element-397" data-mp="3h64p7loetkjh" />
          <path d="M18 15h1.5a2.5 2.5 0 0 1 0 5H18" data-id="element-398" data-mp="3h3a2tolu5pnf" />
        </>}
    </svg>;
};